import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import AsyncSelect from 'react-select/async';
const GenericAsyncSelect = forwardRef((props, ref) => {
    const {
        loadOptions,
        onItemClick,
        onClose,
        placeholder = "Selecione uma opção...",
        value = null,
        dataTest = "async-select",
        customOptionComponent = null,
        style = {},
        maintainInputValue = false,
        onInputValueChange = null,
        cacheUniqs = [],
    } = props;

    const [selectedValue, setSelectedValue] = useState(value);
    const [inputValue, setInputValue] = useState(() => value?.label ?? '');
    const selectRef = useRef(null);

    useImperativeHandle(ref, () => ({
        blur: () => {
            selectRef.current?.blur?.();
        },
        focus: () => {
            selectRef.current?.focus?.();
        },
    }));

    useEffect(() => {
        if (!maintainInputValue) {
            const nextValue = value?.value ?? value;
            const currentValue = selectedValue?.value ?? selectedValue;
            if (nextValue !== currentValue || value?.label !== selectedValue?.label) {
                setSelectedValue(value);
            }
            if (value?.label) {
                setInputValue(value.label);
            } else if (!value) {
                setInputValue('');
            }
            return;
        }
        if (value?.label) {
            setInputValue(value.label);
        }
    }, [value, maintainInputValue]);

    const handleChange = (selectedOption) => {
        if (!maintainInputValue) {
            setSelectedValue(selectedOption);
        } else {
            setSelectedValue(null);
        }

        if (selectedOption && onItemClick) {
            onItemClick(selectedOption.value);
        }

        if (selectedOption && maintainInputValue) {
            const label = selectedOption.label || String(selectedOption.value ?? '');
            setInputValue(label);
        } else if (!maintainInputValue) {
            setInputValue('');
        } else if (!selectedOption) {
            setInputValue('');
            if (onInputValueChange) {
                onInputValueChange('');
            }
        }
    };

    const handleInputChange = (newInputValue, {action}) => {
        if (action === 'input-change') {
            setInputValue(newInputValue);
            if (onInputValueChange) {
                onInputValueChange(newInputValue);
            }
        } else if (action === 'set-value') {
            if (!maintainInputValue) {
                setInputValue('');
            }
        } else if (action === 'menu-close' && !maintainInputValue) {
            setInputValue('');
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setInputValue('');
            setSelectedValue(null);
            selectRef.current?.blur?.();
        }
    };

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '33px',
            height: '33px',
            width: style.width || '100%',
            maxWidth: '100%',
            border: state.isFocused ? '1px solid #2980b9' : '1px solid #3498db',
            borderRadius: '5px',
            boxShadow: 'none',
            '&:hover': {
                border: '1px solid #2980b9'
            },
            alignItems: 'center'
        }),
        option: (provided, state) => ({
            ...provided,
            padding: style.optionPadding || '10px 12px',
            minHeight: style.optionMinHeight || '40px',
            display: 'flex',
            alignItems: 'center',
            fontSize: style.optionFontSize || '15px',
            lineHeight: 1.35,
            backgroundColor: state.isFocused ? '#e8f4fc' : 'transparent',
            cursor: 'pointer',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            width: style.menuWidth || style.width || provided.width,
            minWidth: style.menuMinWidth || style.menuWidth || style.width || provided.width,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 10050
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#333'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c757d'
        }),
        indicatorsContainer: () => ({
            display: 'none'
        }),
        dropdownIndicator: () => ({
            display: 'none'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    return (
        <div data-test={dataTest} style={{width: style.width || '100%', maxWidth: '100%'}}>
            <AsyncSelect
                ref={selectRef}
                cacheOptions
                cacheUniqs={cacheUniqs}
                loadOptions={loadOptions}
                onChange={handleChange}
                onInputChange={handleInputChange}
                onMenuClose={() => {
                    if (onClose) {
                        onClose();
                    }
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                value={maintainInputValue ? null : (selectedValue || value)}
                inputValue={inputValue}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customStyles}
                noOptionsMessage={() => null}
                loadingMessage={() => "Carregando..."}
                isClearable
                isSearchable
                blurInputOnSelect={false}
                controlShouldRenderValue={false}
                defaultMenuIsOpen={false}
                components={customOptionComponent ? {
                    Option: customOptionComponent
                } : undefined}
            />
        </div>
    );
});

GenericAsyncSelect.displayName = 'GenericAsyncSelect';

export default GenericAsyncSelect;
